/**
 * Verktøyene til tall-agenten (VIB-2992). Hvert verktøy gjør om dataene
 * widgetene allerede har regnet ut til et kompakt objekt modellen leser.
 * Ingen egne formler her: tallene hentes fra de samme funksjonene som
 * Måltall, så svaret kan ikke vise et annet tall enn widgeten.
 *
 * Navnene må matche AGENT_TOOLS i supabase/functions/maaltall-agent/logic.ts.
 */
import { calcBillingRate } from '@/lib/billing-rates-calc';
import { dayRows, unlogged } from '@/lib/capacity-breakdown';
import {
  customersWithSeveralOffers,
  distinctPipeOffers,
  pipeAgeBuckets,
  type AdvisorPipe,
  type PipeTotals,
} from '@/lib/offers-in-pipe';
import type { TeamFaktModel } from '@/components/dashboard/widgets/team-fakt-model';
import type { CheckFinding } from '@/lib/maaltall-checks';
import type { BonusGoal } from '@/hooks/use-bonus-goals';
import type { ForecastMonth } from '@/lib/invoice-forecast-calc';
import type { ApprovedTaskDeviationResult } from '@/lib/approved-task-deviation-calc';
import type { ResultMonth } from '@/lib/kpi-calculations';
import { MONTH_NAMES_NB } from '@/lib/format';

export type AgentToolName =
  | 'faktureringsgrad'
  | 'ikke_fort'
  | 'budsjettavvik'
  | 'bonusmal'
  | 'prognose'
  | 'pipeline'
  | 'sjekker'
  | 'noekkeltall';

/** Kildene som vises under svaret, per verktøy. */
export const TOOL_LABELS: Record<AgentToolName, string> = {
  faktureringsgrad: 'Faktureringsgrad',
  ikke_fort: 'Ikke ført',
  budsjettavvik: 'Budsjettavvik',
  bonusmal: 'Bonusmål',
  prognose: 'Prognose',
  pipeline: 'GEEK i tilbudspipe',
  sjekker: 'Dette bør du se på',
  noekkeltall: 'Nøkkeltall',
};

/** Widgeten på Måltall som viser tallene bak hvert verktøy. */
export const TOOL_WIDGET: Record<AgentToolName, string> = {
  faktureringsgrad: 'faktureringsgrad-team',
  ikke_fort: 'faktureringsgrad-team',
  budsjettavvik: 'budsjettavvik-kompakt',
  bonusmal: 'bonusmal-kompakt',
  prognose: 'prognose-fakturering',
  pipeline: 'maaltall-kpi',
  sjekker: 'maaltall-sjekker',
  noekkeltall: 'maaltall-kpi',
};

export interface AgentToolInputs {
  today: string;
  faktModel: TeamFaktModel | null;
  taskDeviation: (ApprovedTaskDeviationResult & { names: Map<string, string> }) | null;
  goals: BonusGoal[] | null;
  forecast: ForecastMonth[] | null;
  pipe: { totals: PipeTotals; pipes: AdvisorPipe[] } | null;
  findings: CheckFinding[] | null;
  revenue: { current: number[]; previousYear: number[] } | null;
  result: ResultMonth[] | null;
  soldByMonth: number[] | null;
}

const r1 = (n: number) => Math.round(n * 10) / 10;
const kr = (n: number) => Math.round(n);
const monthName = (m: number) => MONTH_NAMES_NB[m - 1];
const unavailable = { feil: 'Dataene er ikke lastet. Svar at tallet mangler.' };

function faktureringsgrad(model: TeamFaktModel) {
  const t = model.team;
  const internal = new Map<string, number>();
  for (const c of model.breakdown.classified) {
    if (c.category !== 'internt' || !model.months.includes(c.month) || !c.projectId) continue;
    const name = model.breakdown.projects.get(c.projectId)?.name ?? 'Ukjent prosjekt';
    internal.set(name, (internal.get(name) ?? 0) + c.hours);
  }
  return {
    periode: `januar–${monthName(model.months[model.months.length - 1] ?? 1)} ${model.year}`,
    regel: 'Fakturerbart (solgte timer på godkjente oppgaver, i måneden de godkjennes) delt på kapasitet etter fravær og helligdager. Bare ansatte med kapasitet.',
    team: { grad_pct: r1(model.rate), maal_pct: model.target, fakturerbart_t: r1(model.billable), kapasitet_t: r1(t.capacity) },
    kapasitetsfordeling_t: {
      godkjente_oppgaver: r1(t.godkjent), interne_prosjekter: r1(t.internt), paagaar_eller_venter: r1(t.venter),
      kundeprosjekt_uten_oppgave: r1(t.uten), ikke_fort: r1(unlogged(t)),
    },
    ikke_fort_forklaring: {
      regel: 'Ikke ført er netto, slik widgeten viser det: kapasitet uten føring minus timer ført utover kapasiteten. Bruk nettotallet når du oppgir andel av kapasiteten.',
      kapasitet_uten_foering_t: r1(t.under), fort_over_kapasitet_t: r1(t.over),
    },
    grad_hvis_alt_som_venter_godkjennes_pct: r1(calcBillingRate(model.billable + t.venter, t.capacity) ?? 0),
    stoerste_interne_prosjekter_t: [...internal.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8).map(([navn, t]) => ({ navn, timer: r1(t) })),
    per_maaned: model.months.map((m) => {
      const s = model.sliceFor(model.employees.map((e) => e.userId), [m]);
      const billable = model.billableFor(null, [m]);
      return { maaned: monthName(m), grad_pct: r1(calcBillingRate(billable, s.capacity) ?? 0), kapasitet_t: r1(s.capacity), ikke_fort_netto_t: r1(unlogged(s)), uten_foering_t: r1(s.under), interne_t: r1(s.internt), venter_t: r1(s.venter) };
    }),
    per_ansatt: model.employees.map((e) => {
      const s = model.sliceFor([e.userId], model.months);
      const billable = model.billableFor(e.userId, model.months);
      return {
        navn: e.fullName,
        grad_pct: r1(calcBillingRate(billable, s.capacity) ?? 0), eget_maal_pct: e.targetRate,
        kapasitet_t: r1(s.capacity), godkjent_t: r1(s.godkjent), interne_t: r1(s.internt), venter_t: r1(s.venter),
        uten_oppgave_t: r1(s.uten), ikke_fort_netto_t: r1(unlogged(s)), uten_foering_t: r1(s.under),
        aapne_maaneder: e.months.filter((m) => model.months.includes(m.month) && !m.confirmed).map((m) => monthName(m.month)),
      };
    }),
  };
}

function ikkeFort(model: TeamFaktModel, today: string) {
  return {
    regel: 'Ukedager før i dag uten førte timer og uten registrert fravær. Helligdager er trukket fra. Deltidsansatte kan ha fridager her.',
    per_ansatt: model.employees.map((e) => {
      const months = model.months.map((month) => {
        const rows = dayRows({
          year: model.year, month,
          hoursByDate: model.breakdown.hoursByDay.get(e.userId) ?? new Map(),
          holidays: model.breakdown.holidays,
          absences: model.breakdown.absences.filter((a) => a.user_id === e.userId),
          today,
        });
        return { maaned: monthName(month), dager_uten_foering: rows.filter((r) => r.kind === 'mangler').map((r) => r.date) };
      });
      return { navn: e.fullName, dager_totalt: months.reduce((s, m) => s + m.dager_uten_foering.length, 0), maaneder: months.filter((m) => m.dager_uten_foering.length > 0) };
    }).filter((e) => e.dager_totalt > 0).sort((a, b) => b.dager_totalt - a.dager_totalt),
  };
}

function budsjettavvik(d: NonNullable<AgentToolInputs['taskDeviation']>) {
  const projects = d.employees.flatMap((e) => e.projects);
  const byProject = new Map<string, { navn: string; solgt: number; brukt: number }>();
  for (const p of projects) {
    const row = byProject.get(p.projectId) ?? { navn: [p.projectCode, p.projectName].filter(Boolean).join(' '), solgt: 0, brukt: 0 };
    row.solgt += p.assignedHours;
    row.brukt += p.loggedHours;
    byProject.set(p.projectId, row);
  }
  const rows = [...byProject.values()].filter((p) => p.solgt > 0).map((p) => ({ ...p, avvik_pct: r1(((p.brukt - p.solgt) / p.solgt) * 100) }));
  return {
    regel: '(brukt − solgt) ÷ solgt på godkjente oppgaver i år. Positivt = brukt mer enn solgt. Rød over +5 % for teamet.',
    team: { avvik_pct: d.team.pct == null ? null : r1(d.team.pct), oppgaver: d.team.taskCount, solgt_t: r1(d.team.assignedHours), brukt_t: r1(d.team.loggedHours) },
    per_ansatt: d.employees.map((e) => ({ navn: d.names.get(e.userId) ?? 'Ukjent', avvik_pct: e.pct == null ? null : r1(e.pct), oppgaver: e.taskCount, solgt_t: r1(e.assignedHours), brukt_t: r1(e.loggedHours) })),
    mest_over: [...rows].sort((a, b) => b.avvik_pct - a.avvik_pct).slice(0, 5).map((p) => ({ ...p, solgt: r1(p.solgt), brukt: r1(p.brukt) })),
    mest_under: [...rows].sort((a, b) => a.avvik_pct - b.avvik_pct).slice(0, 5).map((p) => ({ ...p, solgt: r1(p.solgt), brukt: r1(p.brukt) })),
  };
}

function pipeline(p: NonNullable<AgentToolInputs['pipe']>, today: string) {
  const offers = distinctPipeOffers(p.pipes);
  return {
    geek_kr: kr(p.totals.bfKr), vektet_geek_kr: kr(p.totals.weightedBfKr), tilbud: p.totals.offerCount,
    uten_sannsynlighet: p.totals.uncategorizedCount, uten_close_dato: p.totals.missingCloseDateCount,
    alder: pipeAgeBuckets(offers, new Date(`${today}T12:00:00Z`)).map((b) => ({ alder: b.label, tilbud: b.offers.length, geek_kr: kr(b.bfKr) })),
    kunder_med_flere_tilbud: customersWithSeveralOffers(offers).slice(0, 8).map((c) => ({ kunde: c.customer, tilbud: c.offers.length, geek_kr: kr(c.bfKr) })),
    stoerste: [...offers].sort((a, b) => b.bfKr - a.bfKr).slice(0, 10).map((o) => ({ tittel: o.title, kunde: o.customerName, geek_kr: kr(o.bfKr), sendt: o.sentAt?.slice(0, 10) ?? null })),
  };
}

/** Resultatet av ett verktøy, klart til å sendes (før pseudonymisering). */
export function runAgentTool(name: string, i: AgentToolInputs): unknown {
  switch (name as AgentToolName) {
    case 'faktureringsgrad': return i.faktModel ? faktureringsgrad(i.faktModel) : unavailable;
    case 'ikke_fort': return i.faktModel ? ikkeFort(i.faktModel, i.today) : unavailable;
    case 'budsjettavvik': return i.taskDeviation ? budsjettavvik(i.taskDeviation) : unavailable;
    case 'bonusmal':
      return i.goals ? i.goals.map((g) => ({ maal: g.title, status: g.achieved ? 'nådd' : 'ikke nådd', naa: r1(g.current), maal_verdi: g.target, enhet: g.unit, retning: g.targetDirection === 'min' ? 'minst' : 'høyst', belop_kr: g.amountNok })) : unavailable;
    case 'prognose':
      return i.forecast ? i.forecast.map((m) => ({ maaned: m.longLabel, omsetning_fakturert: kr(m.omsFakt), omsetning_rest: kr(m.omsRest), geek_fakturert: kr(m.bfFakt), geek_rest: kr(m.bfRest) })) : unavailable;
    case 'pipeline': return i.pipe ? pipeline(i.pipe, i.today) : unavailable;
    case 'sjekker':
      return i.findings ? i.findings.map((f) => ({ sjekk: f.title, forklaring: f.body, hvem: f.who, verdi: f.value, enhet: f.unit, regel: f.rule, eksempler: f.items.slice(0, 10).map((it) => ({ tittel: it.title, detalj: it.sub, verdi: it.value })) })) : unavailable;
    case 'noekkeltall':
      return {
        omsetning_per_maaned: i.revenue ? i.revenue.current.map((v, m) => ({ maaned: monthName(m + 1), i_aar_kr: kr(v), i_fjor_kr: kr(i.revenue!.previousYear[m] ?? 0) })) : null,
        resultat_per_maaned: i.result && i.result.length > 0 ? i.result.map((r, m) => ({ maaned: monthName(m + 1), inntekt_kr: kr(r.revenue), kostnad_kr: kr(r.cost), resultat_kr: kr(r.result) })) : 'PowerOffice svarte ikke',
        geek_solgt_per_maaned: i.soldByMonth ? i.soldByMonth.map((v, m) => ({ maaned: monthName(m + 1), geek_kr: kr(v) })) : null,
      };
    default: return { feil: `Ukjent verktøy ${name}` };
  }
}
